/**
 * Launch-argument assembly for the MineZero Unreal app.
 *
 * The launcher EXE applies only the user's PERSONAL args (`my_launch_options`)
 * on its own. Everything that tells the app *which backend to talk to* — org,
 * server URL, WorkOS org, project — must arrive in the local-API `/launch`
 * request body (`launch_args`); see turbotwin-launcher
 * `command_processor.rs::get_launch_args_from_subscription`, whose doc comment
 * spells out that tier-2 admin args "come through the local API request body
 * when launched from the webclient".
 *
 * The portal used to send none of them, so the app started with its built-in
 * defaults and sat on "Connection timed out" while the very same build
 * launched from the TurboTwin webclient connected fine (Cloud Lead
 * 2026-07-29). This mirrors the webclient's `buildSessionLaunchArgs` +
 * channel-args composition so both entry points hand the app the same context.
 *
 * NOTE: the SpacetimeDB `queue_launch` fallback has NO args parameter at all,
 * so a launch queued while the local API is unreachable still starts arg-less.
 * That limitation is the launcher module's, and the webclient shares it.
 */

/**
 * Session context args, skipped individually when `existingArgs` already
 * carries an explicit override (admin channel args win over derived values).
 */
export function buildSessionLaunchArgs(args: {
  orgName: string | null;
  serverUrl: string | null;
  workosOrgId?: string | null;
  /** The topbar's current site (project module name). With `-TurboTwinProject=`
   *  on the command line the Unreal client auto-connects and skips its globe
   *  project selector. An explicit admin channel override still wins. */
  projectName?: string | null;
  existingArgs?: string;
}): string {
  const existing = args.existingArgs ?? '';
  const parts: string[] = [];
  if (args.orgName && !existing.includes('-TurboTwinOrg=')) {
    parts.push(`-TurboTwinOrg=${args.orgName}`);
  }
  if (args.serverUrl && !existing.includes('-TurboTwinServerURL=')) {
    parts.push(`-TurboTwinServerURL=${args.serverUrl}`);
  }
  if (args.workosOrgId && !existing.includes('-TurboTwinWorkOSOrgId=')) {
    parts.push(`-TurboTwinWorkOSOrgId=${args.workosOrgId}`);
  }
  if (args.projectName && !existing.includes('-TurboTwinProject=')) {
    parts.push(`-TurboTwinProject=${args.projectName}`);
  }
  return parts.join(' ');
}

/** Minimal shape of an `app_channel_config` row. */
interface AppChannelConfigLike {
  appId: string;
  channel: { tag: string };
  launchArgs?: string | undefined;
  displayName?: string | undefined;
}

/** Channels an admin has configured for the app, in the webclient's display
 *  order (Stable first). Empty when the subscription is unavailable. */
export function configuredChannels<C extends string>(
  configs: readonly AppChannelConfigLike[],
  appId: string,
  order: readonly C[],
): C[] {
  return order.filter((ch) =>
    configs.some((c) => c.appId === appId && c.channel?.tag === ch),
  );
}

/** Admin display name for the channel, else the enum tag (webclient parity). */
export function channelLabel(
  configs: readonly AppChannelConfigLike[],
  appId: string,
  channel: string,
): string {
  const cfg = configs.find((c) => c.appId === appId && c.channel?.tag === channel);
  return cfg?.displayName?.trim() || channel;
}

/**
 * Admin-configured per-channel args (tier 2), e.g.
 * `-TurboTwinProject=minezero-sandbox`. Matched on (appId, channel) exactly as
 * the webclient's `resolveChannelLaunch` does.
 */
export function resolveChannelArgs(
  configs: readonly AppChannelConfigLike[],
  appId: string,
  channel: string,
): string {
  const cfg = configs.find(
    (c) => c.appId === appId && c.channel?.tag === channel,
  );
  return cfg?.launchArgs?.trim() ?? '';
}

/** Full arg string for a launch: session context + admin channel args. */
export function composeLaunchArgs(args: {
  orgName: string | null;
  serverUrl: string | null;
  workosOrgId?: string | null;
  projectName?: string | null;
  channelArgs: string;
}): string {
  const session = buildSessionLaunchArgs({
    orgName: args.orgName,
    serverUrl: args.serverUrl,
    workosOrgId: args.workosOrgId,
    projectName: args.projectName,
    existingArgs: args.channelArgs,
  });
  return [session, args.channelArgs].filter(Boolean).join(' ');
}

/**
 * Observer launch (COHORT-SCHEDULING-PLAN Implementation contracts):
 * the trainee launch args plus `-TurboTwinObserveSession=<mp session_id>`.
 * On seeing that argument the MineZero client enters the session as an
 * observer — no assignment lookup, HUD hidden, free camera, chat
 * read-only. The argument is REQUIRED for the mode: a launch that drops
 * it (the SpacetimeDB queue fallback carries no args at all) starts a
 * normal trainee session instead, so observer launches must go through
 * the local launcher API only.
 */
export function buildObserverLaunchArgs(args: {
  orgName: string | null;
  serverUrl: string | null;
  workosOrgId?: string | null;
  projectName?: string | null;
  channelArgs: string;
  sessionId: bigint;
}): string {
  const base = composeLaunchArgs({
    orgName: args.orgName,
    serverUrl: args.serverUrl,
    workosOrgId: args.workosOrgId,
    projectName: args.projectName,
    channelArgs: args.channelArgs,
  });
  return [base, `-TurboTwinObserveSession=${args.sessionId}`]
    .filter(Boolean)
    .join(' ');
}
